import React, { useEffect, useMemo, useState } from 'react';
import {
    ResourcesPageQuery,
    ResourcesSortableFields,
    ColumnOrder,
    useResourcesPageLazyQuery,
} from '../../../../../services';
import {
    CTableWrapper,
    DBlock,
    DeleteIcon,
    DPage,
    DPageBody,
    DPaginator,
    DTextField,
    EditIcon,
    SortedOrder,
    usePaginator,
} from '../../../../dum';
import {
    SDeleteResourceDialog,
    SUpsertResourceDialog,
} from '../../../../smart';
import { Box, IconButton, Typography } from '@mui/material';
import {
    COLUMN_LABELS,
    FIELD_LABELS,
    shortLabel,
    useDebounce,
    useString,
} from '../../../../../helpers';

export function ResourcesPage() {
    const [query, { data, previousData, loading }] =
        useResourcesPageLazyQuery();
    const [upsertOpen, setUpsertOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [resourceId, setResourceId] = useState<number | null>(null);

    const [sortedIndex, setSortedIndex] = useState<number | null>(null);
    const [sortedOrder, setSortedOrder] = useState<SortedOrder>(null);

    const [filter, setFilter] = useString({
        KEY: ResourcesPage.name + '_Filter',
    });

    const debouncedFilter = useDebounce(filter, 400);

    const { setSkip, skip, take } = usePaginator(
        {
            KEY: ResourcesPage.name + '_Paginator',
        },
        [debouncedFilter],
    );

    useEffect(() => {
        void query({
            variables: {
                skip: skip,
                take: take,
                filter: debouncedFilter,
                sortField:
                    sortedIndex === 0 ? ResourcesSortableFields.Name : null,
                sortOrder:
                    sortedOrder === 'asc'
                        ? ColumnOrder.Asc
                        : sortedOrder === 'desc'
                        ? ColumnOrder.Desc
                        : null,
            },
        });
    }, [query, skip, take, debouncedFilter, sortedOrder, sortedIndex]);

    const resources =
        data?.paginatedResources.docs ||
        previousData?.paginatedResources.docs ||
        [];

    const count =
        data && data?.paginatedResources.count >= 0
            ? data?.paginatedResources.count
            : previousData?.paginatedResources.count;

    const handleEditResource = useMemo(() => {
        return (
            resource: ResourcesPageQuery['paginatedResources']['docs'][number],
        ) => {
            setResourceId(resource.id);
            setUpsertOpen(true);
        };
    }, []);

    const handleDeleteResource = useMemo(() => {
        return (
            resource: ResourcesPageQuery['paginatedResources']['docs'][number],
        ) => {
            setResourceId(resource.id);
            setDeleteOpen(true);
        };
    }, []);

    return (
        <DPage>
            <DPageBody>
                <DBlock>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Recursos</Typography>
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <DTextField
                                label={'Filtro'}
                                helperText={'Nombre del recurso.'}
                                value={filter}
                                onChange={(value) => {
                                    setFilter(value || '');
                                }}
                                hideHelperTextBelowLg
                            />
                        </Box>
                        <DPaginator skip={skip} take={take} setSkip={setSkip} count={count} disableGutters />
                    </Box>
                    <CTableWrapper
                        sortedIndex={sortedIndex}
                        setSortedIndex={setSortedIndex}
                        sortedOrder={sortedOrder}
                        setSortedOrder={setSortedOrder}
                        contextItems={[
                            {
                                title: shortLabel(COLUMN_LABELS.edit),
                                callback: (resource) => {
                                    handleEditResource(resource);
                                },
                            },
                            {
                                title: shortLabel(COLUMN_LABELS.delete),
                                disable: (resource) => {
                                    return !resource.is_deletable;
                                },
                                callback: (resource) => {
                                    handleDeleteResource(resource);
                                },
                            },
                        ]}
                        items={[
                            {
                                title: shortLabel(FIELD_LABELS.name),
                                cellGrow: 45,
                                sortable: true,
                                content: (resource) => {
                                    return resource.name;
                                },
                            },
                            {
                                title: shortLabel(
                                    FIELD_LABELS.resource_category_id,
                                ),
                                cellGrow: 45,
                                content: (resource) => {
                                    return resource?.resource_category?.name;
                                },
                            },
                            {
                                title: shortLabel(FIELD_LABELS.updated_by_id),
                                cellGrow: 30,
                                content: (resource) => {
                                    return (
                                        resource?.updated_by?.fullname || '—'
                                    );
                                },
                            },
                            {
                                title: '',
                                textAlign: 'center',
                                content: (resource) => {
                                    return (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 0.5, justifyContent: 'center',
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                sx={{ bgcolor: 'rgba(34,197,94,0.15)', boxShadow: 2, borderRadius: '50%' }}
                                                onClick={() => {
                                                    handleEditResource(
                                                        resource,
                                                    );
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                sx={{ bgcolor: 'rgba(239,68,68,0.15)', boxShadow: 2, borderRadius: '50%' }}
                                                disabled={
                                                    !resource.is_deletable
                                                }
                                                onClick={() => {
                                                    handleDeleteResource(
                                                        resource,
                                                    );
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    );
                                },
                            },
                        ]}
                        data={resources}
                        loading={loading}
                    />
                </DBlock>
            </DPageBody>
            <SUpsertResourceDialog
                open={upsertOpen}
                setOpen={setUpsertOpen}
                resourceId={resourceId}
            />
            <SDeleteResourceDialog
                open={deleteOpen}
                setOpen={setDeleteOpen}
                resourceId={resourceId}
            />
        </DPage>
    );
}
